import React from 'react';
import { createRoot } from 'react-dom/client';

import BayDialog from './BayDialog';

/*
 * Imperative async confirm dialog. Replaces `react-confirm` (removed 2026-08-10).
 *
 * WHY IT IS OURS NOW
 * ------------------
 * `react-confirm` was pinned at 0.3.1, a prerelease line, and all it provided was
 * ~40 lines of "render a component into a detached node, hand it a promise's
 * resolve/reject, unmount later". That is this file. Owning it means the promise
 * semantics, the teardown timing and the props contract are all readable here
 * instead of split across a library's `createConfirmation` and a `confirmable`
 * HOC that injected three undocumented props.
 *
 * BEHAVIOUR IS UNCHANGED ON PURPOSE. Read the next paragraph before touching it.
 *
 * CANCEL REJECTS. IT DOES NOT RESOLVE FALSE.
 * -----------------------------------------
 * `react-confirm`'s `confirmable` called `reject()` on cancel, so "the user said
 * no" arrives as an exception, and all 14 call sites are built on that -- they sit
 * inside `try`/`catch` and treat the catch as the cancel branch.
 *
 * That API is not good, but it CANNOT be changed here in isolation. If this
 * resolved `false` instead, every one of those `try` blocks would fall straight
 * through to the confirmed path and delete on cancel. Eleven of the fourteen are
 * destructive ("Delete Note", "Remove Plan", "Deny lead").
 *
 * The safe migration, if someone wants sane semantics: add a second export that
 * resolves `true`/`false`, move call sites to it one at a time deleting their
 * `try`/`catch` as they go, and remove this one when the last caller is gone. Do
 * not flip this function's behaviour under the existing callers.
 *
 * (`EditPrimaryWidget.js` reads `if (await BayDialogConfirm(...))`, which looks
 * like it handles a falsy result but cannot -- on cancel it throws to the
 * surrounding catch. It works, but not for the reason it appears to.)
 *
 * TEARDOWN
 * --------
 * The dialog is unmounted on the modal's `onExited`, not on a timer.
 * `react-confirm` used a hardcoded 1000ms `unmountDelay` to let the exit
 * animation play, which is both a guess and a leak if the transition is longer.
 * `BayModal` now forwards `onExited`, so teardown happens exactly when the
 * animation ends.
 *
 * The unmount is deferred a tick. Calling `root.unmount()` synchronously from
 * inside a React lifecycle callback warns ("Attempted to synchronously unmount a
 * root while React was already rendering") and can drop the final commit.
 */

interface BayDialogConfirmArgs {
	confirmation?: React.ReactNode;
	options?: Record<string, unknown>;
}

/**
 * Show a confirm dialog and wait for the user.
 *
 * Resolves `true` when confirmed. **Rejects** when cancelled, dismissed, or
 * closed -- see the note above.
 */
export const BayDialogConfirm = ({ confirmation, options }: BayDialogConfirmArgs = {}) =>
	new Promise((resolve, reject) => {
		const host = document.createElement('div');
		host.setAttribute('data-bay-dialog-host', '');
		document.body.appendChild(host);

		const root = createRoot(host);

		// Guards against a second settle. `onExited` can fire after the promise has
		// already settled (a caller may unmount the page mid-transition), and
		// settling twice is a silent no-op that would otherwise mask a double
		// teardown.
		let settled = false;
		let teardownScheduled = false;

		const teardown = () => {
			if (teardownScheduled) return;
			teardownScheduled = true;
			// Deferred: see TEARDOWN above.
			setTimeout(() => {
				root.unmount();
				host.remove();
			}, 0);
		};

		// Re-render with `show: false` so the modal animates out, then let its
		// `onExited` trigger teardown. Settling the promise first keeps the caller
		// responsive rather than waiting on an animation.
		const close = (settle, value) => {
			if (settled) return;
			settled = true;
			settle(value);
			render(false);
		};

		// `createElement` rather than JSX, deliberately: this file is `.js`, and it
		// is bundled by three different pipelines (bay-ui's Vite, client's esbuild,
		// smc's esbuild). Not all of them are configured to transform JSX in a
		// `.js` extension, and a rename would change the resolution of every
		// `from '../BayDialog'` import. `createElement` sidesteps both questions.
		function render(show) {
			root.render(
				React.createElement(BayDialog, {
					show,
					confirmation,
					options,
					proceed: (value) => close(resolve, value ?? true),
					cancel: (value) => close(reject, value),
					onExited: teardown,
				}),
			);
		}

		render(true);
	});

export default BayDialogConfirm;
